import {
  Box,
  Divider,
  MenuItem,
  MenuList,
  Popover,
  Typography,
} from '@mui/material';

import { useAuthStore } from '@/store/auth/auth.store';
import { useUiStore } from '@/store/ui/ui.store';
import { useDiccionarioMotivo } from '@/store/erp/diccionario_motivo/diccionario_motivo.store';
import { useNavigate } from 'react-router-dom';
import { ROUTER_PATHS } from '@/shared/constants/routes/router-paths.constants';
import { useFetchUsuarioResumen } from '@/shared/actions/erp/tickets-soporte-cliente';

// Permisos del grupo "Base de conocimiento" (Gestión de Accesos → Roles). Con
// cualquiera de ellos se muestra la opción "Documentación" del menú.
const PERMISOS_DOCUMENTACION = [
  'tickets.documentacion_usuario',
  'tickets.documentacion_admin',
  'tickets.documentacion_admin_ti',
  'tickets.editar_documentacion',
];

export type AccountPopoverProps = {
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
};

const AccountPopover: React.FC<AccountPopoverProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const user = useAuthStore(s => s.user);
  const logOut = useAuthStore(s => s.logout);
  const setOpenPopover = useUiStore(s => s.setOpenPopover);
  const navigate = useNavigate();

  const socket = useDiccionarioMotivo(s => s.socket);

  // ¿Puede abrir la base de conocimiento? (superusuario o algún permiso de documentación)
  const isSuperUser = !!(user as any)?.is_superuser;
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const { data: resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = resumenRes as any;
  const permisos: string[] = _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  const canVerDocumentacion = isSuperUser || PERMISOS_DOCUMENTACION.some((p) => permisos.includes(p));

  const handleSignOut = async () => {
    setOpenPopover(false);
    if (socket) {
      socket.disconnect()
    }
    
    logOut().then((_e) => {
      window.location.reload();
    });
  };

  const handleGoToProfile = async () => {
    const URL_PROFILE = ROUTER_PATHS.perfil.root
    navigate(`${URL_PROFILE}`);
  }

  // Abre la base de conocimiento / manual de tickets en una PESTAÑA NUEVA
  // (frontend aparte, sin el layout normal).
  const handleGoToDocumentacion = () => {
    setOpenPopover(false);
    window.open('/documentacion/tickets', '_blank', 'noopener,noreferrer');
  }

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: {
            width: 213,
          },
        },
      }}
    >
      <Box
        sx={{
          py: 1.5,
          px: 2,
        }}
      >
        <Typography variant="overline">Cuenta</Typography>
        <Typography color="text.secondary" variant="body2">
          {user?.username ?? user?.email ?? user?.identificacion ?? 'Usuario'}
        </Typography>
      </Box>
      <Divider />
      <MenuList
        disablePadding
        dense
        sx={{
          p: '8px',
          '& > *': {
            borderRadius: 1,
          },
        }}
      >
        <MenuItem onClick={handleGoToProfile}>Perfil</MenuItem>
        {canVerDocumentacion && (
          <MenuItem onClick={handleGoToDocumentacion}>Documentación</MenuItem>
        )}
        <MenuItem onClick={handleSignOut}>Cerrar Sesión</MenuItem>
      </MenuList>
    </Popover>
  );
};

export default AccountPopover;
